import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import { TransferDetail, TransferSummary } from "../../../../services/v2/stockops.service";
import ReasonPrompt from "../../../../components/kits/reasonPrompt";
import ProductPicker from "../products/productPicker";
import { Empty, Loading, Pager, apiError, openBlob, ui } from "../kits/shared";

/*
	Bodegas → Traslados (sección "transfers"). Solicitado → En tránsito (despacho con foto) → Recibido (con foto).
	Quien despacha no recibe. Sin <label>: el CSS global los posiciona absolutos.
*/

const S = { REQUESTED: 1, IN_TRANSIT: 2, RECEIVED: 3, DIFFERENCES: 4, CANCELLED: 5 };
const FILTERS = [
	{ key: "open", label: "Por despachar y en tránsito", statusIds: [S.REQUESTED, S.IN_TRANSIT] },
	{ key: "done", label: "Recibidos", statusIds: [S.RECEIVED, S.DIFFERENCES] },
	{ key: "all", label: "Todos", statusIds: [] as number[] }
];

const chip = (statusId: number, text: string, late?: boolean) => {
	const style = late ? { color: "var(--wa-oro)", background: "var(--wa-oro-suave)" }
		: statusId === S.IN_TRANSIT ? { color: "var(--wa-tinta)", background: "var(--wa-papel-3)" }
		: statusId === S.REQUESTED ? { color: "var(--wa-acento-texto)", background: "var(--wa-acento-suave)" }
		: statusId === S.RECEIVED ? { color: "var(--wa-verde)", background: "var(--wa-verde-suave)" }
		: statusId === S.DIFFERENCES ? { color: "var(--wa-oro)", background: "var(--wa-oro-suave)" }
		: { color: "var(--wa-apagado)", background: "var(--wa-papel-2)" };
	return <span style={{ ...style, borderRadius: "10px", padding: "1px 9px", fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap" }}>{late ? "Atrasado · " : ""}{text}</span>;
};

export const Modal: React.FC<{ width?: string; busy?: boolean; onClose: () => void; children: React.ReactNode }> = ({ width, busy, onClose, children }) => {
	useEffect(() => {
		const onKey = (event: KeyboardEvent) => { if (event.key === "Escape" && !busy) onClose(); };
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [busy, onClose]);
	return <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
		onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
		<div className="card" style={{ width: "100%", maxWidth: width || "720px", maxHeight: "94vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(43,28,22,0.25)" }} role="dialog" aria-modal="true">
			<div className="card-body">{children}</div>
		</div>
	</div>;
};

export const PhotoInput: React.FC<{ caption: string; photos: File[]; busy?: boolean; onChange: (photos: File[]) => void }> = ({ caption, photos, busy, onChange }) => <div style={{ flex: "1 1 260px" }}>
	<span style={ui.caption}>{caption}</span>
	<input type="file" accept="image/*" capture="environment" multiple className="form-control" style={{ fontSize: "12px" }} disabled={busy}
		onChange={(event) => { const picked = event.target.files ? Array.from(event.target.files) : []; onChange([...photos, ...picked]); event.target.value = ""; }} />
	{photos.length > 0 ? <div style={{ ...ui.small, marginTop: "4px" }}>{photos.length} foto{photos.length === 1 ? "" : "s"} · <button type="button" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: "11px" }} onClick={() => onChange([])}>quitar</button></div> : null}
</div>;

/** Casillas para elegir números de serie (máximo `max`). */
export const SerialPicker: React.FC<{ options: string[]; value: string[]; max: number; busy?: boolean; onChange: (value: string[]) => void }> = ({ options, value, max, busy, onChange }) => {
	if (options.length < 1) return <div style={{ ...ui.small, color: "var(--wa-oro)" }}>No hay números de serie disponibles.</div>;
	return <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "4px" }}>
		{options.map((serial) => {
			const checked = value.includes(serial);
			return <span key={serial} onClick={() => { if (busy) return; if (checked) onChange(value.filter((entry) => entry !== serial)); else if (value.length < max) onChange([...value, serial]); }}
				style={{ cursor: "pointer", fontFamily: "monospace", fontSize: "12px", padding: "2px 8px", borderRadius: "10px", border: "1px solid " + (checked ? "var(--wa-acento)" : "var(--wa-filo-2)"), background: checked ? "var(--wa-acento-suave)" : "var(--wa-superficie)" }}>{serial}</span>;
		})}
		<span style={ui.small}>{value.length}/{max}</span>
	</div>;
};

const Traslados: React.FC<{ campusId: number; warehouses: any[]; perms: any; idleLocked: boolean }> = ({ campusId, warehouses, perms, idleLocked }) => {
	const http = HttpManager.getInstance().stockOpsService;
	const [filter, setFilter] = useState<string>("open");
	const [rows, setRows] = useState<TransferSummary[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [lastPage, setLastPage] = useState<number>(1);
	const [page, setPage] = useState<number>(1);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [openId, setOpenId] = useState<number | null>(null);
	const [creating, setCreating] = useState<boolean>(false);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const statusIds = FILTERS.find((entry) => entry.key === filter)?.statusIds || [];
			const response = await http.transfers(campusId, { statusIds: statusIds.join(",") || undefined }, page, 100);
			setRows(response.data.data || []);
			setTotal(response.data.total || 0);
			setLastPage(response.data.last_page || 1);
		} catch (requestError: any) {
			setError(apiError(requestError, "No se pudieron cargar los traslados."));
		} finally {
			setLoading(false);
		}
	}, [campusId, filter, page]);
	useEffect(() => { load(); }, [load]);

	return <>
		<div style={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center", margin: "0 0 10px" }}>
			{FILTERS.map((entry) => <button key={entry.key} type="button" disabled={idleLocked} className={"btn btn-sm rounded-pill " + (entry.key === filter ? "btn-primary" : "btn-outline-secondary")}
				onClick={() => { setFilter(entry.key); setPage(1); }}>{entry.label}</button>)}
			{perms.create ? <button type="button" className="btn btn-primary btn-sm rounded-pill" style={{ marginLeft: "auto" }} disabled={idleLocked} onClick={() => setCreating(true)}>Nuevo Traslado</button> : null}
		</div>
		{notice ? <div className="alert alert-success" style={{ padding: "8px 12px", fontSize: "14px", display: "flex", justifyContent: "space-between" }}><span>{notice}</span><button type="button" className="btn-close" onClick={() => setNotice(null)} /></div> : null}
		<Pager page={page} lastPage={lastPage} total={total} unit="traslados" busy={loading} onPage={setPage} />
		<div className="waScrollX" style={{ ...ui.card, overflowX: "auto" }}>
			{loading ? <Loading /> : error ? <div className="alert alert-danger" style={{ margin: "16px" }}>{error}</div>
				: rows.length < 1 ? <Empty text="Sin traslados." />
				: <table style={{ width: "100%", borderCollapse: "collapse" }}>
					<thead><tr>
						<th style={ui.th}>Traslado</th>
						<th style={ui.th}>De → A</th>
						<th style={ui.th}>Productos</th>
						<th style={{ ...ui.th, ...ui.right }}>Piezas</th>
						<th style={ui.th}>Salió</th>
						<th style={ui.th}>Estado</th>
					</tr></thead>
					<tbody>{rows.map((row) => <tr key={row.id} style={{ cursor: "pointer" }} onClick={() => setOpenId(row.id)}>
						<td style={ui.td}><div style={{ fontFamily: "monospace", fontWeight: 700 }}>{row.folio}</div><div style={ui.tiny}>{row.requestedAt} · {row.requestedBy}</div></td>
						<td style={ui.td}>{row.fromWarehouse} → <b>{row.toWarehouse}</b></td>
						<td style={ui.td}>{row.products.join(", ")}{row.lineCount > 3 ? " y " + (row.lineCount - 3) + " más" : ""}</td>
						<td style={{ ...ui.td, ...ui.right, whiteSpace: "nowrap" }}>
							{row.sent > 0 ? row.sent : row.requested}
							{row.received > 0 || row.missing > 0 ? <div style={row.missing + row.damaged > 0 ? { ...ui.tiny, color: "var(--wa-oro)" } : ui.tiny}>llegaron {row.received}{row.damaged ? " · " + row.damaged + " dañadas" : ""}{row.missing ? " · faltan " + row.missing : ""}</div> : null}
						</td>
						<td style={{ ...ui.td, whiteSpace: "nowrap" }}>{row.dispatchedAt || "-"}{row.expectedAt ? <div style={ui.tiny}>llega {row.expectedAt.split("-").reverse().join("/")}</div> : null}</td>
						<td style={ui.td}>{chip(row.statusId, row.status, row.late)}</td>
					</tr>)}</tbody>
				</table>}
		</div>
		{lastPage > 1 ? <Pager page={page} lastPage={lastPage} total={total} unit="traslados" busy={loading} onPage={setPage} /> : null}

		{creating ? <NewTransfer campusId={campusId} warehouses={warehouses} onCancel={() => setCreating(false)}
			onDone={(transfer, message) => { setCreating(false); setNotice(message); load(); setOpenId(transfer.id); }} /> : null}
		{openId !== null ? <TransferModal campusId={campusId} transferId={openId} perms={perms} onChanged={(message) => { setNotice(message); load(); }} onClose={() => setOpenId(null)} /> : null}
	</>;
};

const NewTransfer: React.FC<{ campusId: number; warehouses: any[]; onDone: (transfer: TransferDetail, message: string) => void; onCancel: () => void }> = ({ campusId, warehouses, onDone, onCancel }) => {
	const physical = warehouses.filter((warehouse) => warehouse.kind !== "virtual");
	const [fromId, setFromId] = useState<string>(String(physical.find((warehouse) => warehouse.kind === "central")?.id || physical[0]?.id || ""));
	const [toId, setToId] = useState<string>("");
	const [expectedAt, setExpectedAt] = useState<string>("");
	const [note, setNote] = useState<string>("");
	const [lines, setLines] = useState<{ articleId: number; name: string; waCode: string | null; quantity: string; serialized: boolean }[]>([]);
	const [dispatchNow, setDispatchNow] = useState<boolean>(false);
	const [photos, setPhotos] = useState<File[]>([]);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const hasSerialized = lines.some((line) => line.serialized);
	// Serials are chosen when dispatching from the transfer: with one, "dispatch now" turns itself off (the box looked unticked
	// but stayed on, and the button was dead with no message).
	const dispatching = dispatchNow && !hasSerialized;
	useEffect(() => { if (hasSerialized) setDispatchNow(false); }, [hasSerialized]);
	// Opened before the warehouses arrived: take the default origin when they do ("Sale de" stayed empty).
	useEffect(() => {
		if (fromId === "" && physical.length > 0) setFromId(String(physical.find((warehouse) => warehouse.kind === "central")?.id || physical[0].id));
	}, [physical.length]);
	const valid = fromId !== "" && toId !== "" && fromId !== toId && lines.length > 0 && lines.every((line) => Number(line.quantity) > 0) && (!dispatching || photos.length > 0);

	const save = async () => {
		setBusy(true);
		setError(null);
		try {
			const response = await HttpManager.getInstance().stockOpsService.createTransfer(campusId, {
				fromWarehouseId: fromId, toWarehouseId: toId, expectedAt, note: note.trim(),
				lines: lines.map((line) => ({ articleId: line.articleId, quantity: Number(line.quantity) }))
			}, dispatching, dispatching ? photos : []);
			onDone(response.data.transfer, response.data.message);
		} catch (requestError: any) {
			setError(apiError(requestError, "No se pudo guardar el traslado."));
		} finally {
			setBusy(false);
		}
	};

	return <Modal width="720px" busy={busy} onClose={onCancel}>
		<h4 style={{ marginBottom: "12px" }}>Nuevo Traslado</h4>
		<div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
			<div style={ui.field("220px")}><span style={ui.caption}>Sale de</span>
				<select className="form-select" style={ui.input} value={fromId} disabled={busy} onChange={(event) => setFromId(event.target.value)}>
					{physical.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}
				</select></div>
			<div style={ui.field("220px")}><span style={ui.caption}>Llega a</span>
				<select className="form-select" style={ui.input} value={toId} disabled={busy} onChange={(event) => setToId(event.target.value)}>
					<option value="">Elige…</option>
					{physical.filter((warehouse) => String(warehouse.id) !== fromId).map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}
				</select></div>
			<div style={ui.field("150px")}><span style={ui.caption}>Llega el</span>
				<input type="date" className="form-control" style={ui.input} value={expectedAt} disabled={busy} onChange={(event) => setExpectedAt(event.target.value)} /></div>
		</div>
		<div style={{ ...ui.card, padding: "10px", marginTop: "12px" }}>
			{lines.map((line, index) => <div key={line.articleId} style={{ display: "flex", gap: "8px", alignItems: "center", padding: "4px 0", borderBottom: "1px solid var(--wa-filo)" }}>
				<span style={{ flex: 1 }}><b>{line.name}</b> <span style={ui.tiny}>{line.waCode}{line.serialized ? " · con número de serie" : ""}</span></span>
				<input type="number" min={1} className="form-control" style={{ ...ui.input, width: "90px" }} value={line.quantity} disabled={busy}
					onChange={(event) => setLines(lines.map((entry, i) => i === index ? { ...entry, quantity: event.target.value } : entry))} />
				<button type="button" className="btn btn-sm btn-link" style={{ color: "var(--wa-apagado)", padding: 0 }} disabled={busy} onClick={() => setLines(lines.filter((_, i) => i !== index))}>✕</button>
			</div>)}
			<div style={{ marginTop: "6px" }}>
				<ProductPicker campusId={campusId} kinds={["article", "vitrina"]} excludeIds={lines.map((line) => line.articleId)} disabled={busy}
					placeholder="Agregar producto: nombre, código o escanear (una talla concreta)…"
					onPick={(product: any) => {
						if (!product.trackStock) { setError("\"" + product.name + "\" tiene tallas: agrega cada talla."); return; }
						setError(null);
						setLines([...lines, { articleId: product.id, name: product.name, waCode: product.waCode, quantity: "1", serialized: !!product.serialized }]);
					}} />
			</div>
		</div>
		<div style={{ ...ui.field("100%"), marginTop: "10px" }}><span style={ui.caption}>Nota</span>
			<input type="text" className="form-control" style={ui.input} maxLength={1000} value={note} disabled={busy} onChange={(event) => setNote(event.target.value)} /></div>
		<div style={{ display: "flex", gap: "8px", alignItems: "center", marginTop: "10px", cursor: hasSerialized ? "default" : "pointer", opacity: hasSerialized ? 0.6 : 1 }}
			onClick={() => { if (!busy && !hasSerialized) setDispatchNow(!dispatchNow); }}>
			<input type="checkbox" className="form-check-input" checked={dispatching} readOnly style={{ margin: 0 }} />
			<span style={{ fontSize: "13px" }}>Lo despacho ahora desde {physical.find((warehouse) => String(warehouse.id) === fromId)?.name || "la bodega origen"}
				{hasSerialized ? <span style={ui.small}> · con número de serie: se despacha desde el traslado, eligiendo las series</span> : null}</span>
		</div>
		{dispatching ? <div style={{ marginTop: "8px" }}><PhotoInput caption="Fotos de lo que sale *" photos={photos} busy={busy} onChange={setPhotos} /></div> : null}
		{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
		<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
			<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel}>Cancelar</button>
			<button type="button" className="btn btn-primary rounded-pill" disabled={busy || !valid} onClick={save}>{busy ? "Guardando…" : dispatching ? "Despachar" : "Solicitar Traslado"}</button>
		</div>
	</Modal>;
};

const TransferModal: React.FC<{ campusId: number; transferId: number; perms: any; onChanged: (message: string) => void; onClose: () => void }> = ({ campusId, transferId, perms, onChanged, onClose }) => {
	const http = HttpManager.getInstance().stockOpsService;
	const currentUser: any = useSelector((state: RootState) => state.userProfile.value);
	const [transfer, setTransfer] = useState<TransferDetail | null>(null);
	const [action, setAction] = useState<"dispatch" | "receive" | "cancel" | null>(null);
	const [qty, setQty] = useState<Record<number, { a: string; b: string; serials: string[] }>>({});
	const [photos, setPhotos] = useState<File[]>([]);
	const [note, setNote] = useState<string>("");
	const [expectedAt, setExpectedAt] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(() => {
		http.transfer(campusId, transferId).then((response) => setTransfer(response.data)).catch((requestError) => setError(apiError(requestError, "No se pudo abrir el traslado.")));
	}, [campusId, transferId]);
	useEffect(() => { load(); }, [load]);

	if (!transfer) return <Modal onClose={onClose}>{error ? <div className="alert alert-danger">{error}</div> : <Loading />}</Modal>;
	const st = transfer.statusId;
	const isDispatcher = transfer.dispatchedById !== null && Number(currentUser?.id) === transfer.dispatchedById && currentUser?.currentRole !== "superAdmin";

	const begin = (kind: "dispatch" | "receive") => {
		const initial: Record<number, { a: string; b: string; serials: string[] }> = {};
		transfer.lines.forEach((line) => {
			initial[line.id] = kind === "dispatch"
				? { a: String(Math.min(line.requested, line.originOnHand)), b: "", serials: [] }
				: { a: String(line.sent), b: "", serials: line.serials.filter((serial) => serial.status === "in_transit").map((serial) => serial.serial) };
		});
		setQty(initial);
		setPhotos([]);
		setNote("");
		setExpectedAt(transfer.expectedAt || "");
		setError(null);
		setAction(kind);
	};

	const run = async (call: () => Promise<any>) => {
		setBusy(true);
		setError(null);
		try {
			const response = await call();
			setTransfer(response.data.transfer);
			setAction(null);
			onChanged(response.data.message);
		} catch (requestError: any) {
			setError(apiError(requestError, "No se pudo completar."));
		} finally {
			setBusy(false);
		}
	};

	const submit = () => {
		if (action === "dispatch") {
			run(() => http.dispatchTransfer(campusId, transfer.id, transfer.lines.map((line) => ({ lineId: line.id, quantity: Number(qty[line.id]?.a) || 0, serials: qty[line.id]?.serials || [] })), photos, note.trim(), expectedAt));
		} else if (action === "receive") {
			run(() => http.receiveTransfer(campusId, transfer.id, transfer.lines.map((line) => ({ lineId: line.id, received: Number(qty[line.id]?.a) || 0, damaged: Number(qty[line.id]?.b) || 0, serials: qty[line.id]?.serials || [] })), photos, note.trim()));
		}
	};

	return <Modal width="860px" busy={busy} onClose={onClose}>
		<div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "flex-start" }}>
			<div>
				<h4 style={{ margin: 0, fontFamily: "monospace" }}>{transfer.folio} {chip(st, transfer.status, transfer.late)}</h4>
				<div style={{ ...ui.small, marginTop: "4px" }}>{transfer.fromWarehouse} → <b>{transfer.toWarehouse}</b> · solicitado {transfer.requestedAt} por {transfer.requestedBy}
					{transfer.dispatchedAt ? " · despachado " + transfer.dispatchedAt + " por " + transfer.dispatchedBy : ""}{transfer.receivedAt ? " · recibido " + transfer.receivedAt + " por " + transfer.receivedBy : ""}</div>
			</div>
			<button type="button" className="btn-close" onClick={onClose} disabled={busy} />
		</div>
		{action === null && perms.create ? <div style={{ display: "flex", gap: "6px", marginTop: "12px", flexWrap: "wrap" }}>
			{st === S.REQUESTED ? <button type="button" className="btn btn-primary btn-sm rounded-pill" onClick={() => begin("dispatch")}>Despachar</button> : null}
			{st === S.IN_TRANSIT ? <button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={isDispatcher} title={isDispatcher ? "Lo recibe otra persona" : undefined} onClick={() => begin("receive")}>Recibir</button> : null}
			{(st === S.REQUESTED || st === S.IN_TRANSIT) ? <button type="button" className="btn btn-outline-secondary waOroTexto btn-sm rounded-pill" onClick={() => setAction("cancel")}>{st === S.IN_TRANSIT ? "Cancelar (regresa a origen)" : "Cancelar"}</button> : null}
		</div> : null}
		{isDispatcher && st === S.IN_TRANSIT && action === null ? <div style={{ ...ui.small, marginTop: "6px" }}>Tú lo despachaste: lo recibe alguien de {transfer.toWarehouse}.</div> : null}

		<table style={{ width: "100%", borderCollapse: "collapse", marginTop: "12px" }}>
			<thead><tr>
				<th style={ui.th}>Producto</th>
				<th style={{ ...ui.th, ...ui.right }}>Pedido</th>
				<th style={{ ...ui.th, ...ui.right }}>{action === "dispatch" ? "Hay en origen" : "Salió"}</th>
				{action === "dispatch" ? <th style={{ ...ui.th, width: "110px" }}>Sale</th> : null}
				{action === "receive" ? <><th style={{ ...ui.th, width: "110px" }}>Llegó bien</th><th style={{ ...ui.th, width: "90px" }}>Dañadas</th></> : null}
				{action === null && st >= S.RECEIVED ? <th style={{ ...ui.th, ...ui.right }}>Llegó</th> : null}
			</tr></thead>
			<tbody>{transfer.lines.map((line) => <tr key={line.id}>
				<td style={ui.td}>
					<div style={{ fontWeight: 600 }}>{line.name}</div><div style={ui.tiny}>{line.waCode}</div>
					{line.serials.length > 0 && action === null ? <div style={{ ...ui.tiny, fontFamily: "monospace" }}>{line.serials.map((serial) => serial.serial + (serial.status !== "in_stock" ? " (" + (serial.status === "in_transit" ? "en tránsito" : "no llegó") + ")" : "")).join(", ")}</div> : null}
					{line.serialized && action === "dispatch" ? <SerialPicker options={line.availableSerials} value={qty[line.id]?.serials || []} max={Number(qty[line.id]?.a) || 0} busy={busy}
						onChange={(serials) => setQty({ ...qty, [line.id]: { ...qty[line.id], serials } })} /> : null}
					{line.serialized && action === "receive" ? <SerialPicker options={line.serials.filter((serial) => serial.status === "in_transit").map((serial) => serial.serial)} value={qty[line.id]?.serials || []} max={Number(qty[line.id]?.a) || 0} busy={busy}
						onChange={(serials) => setQty({ ...qty, [line.id]: { ...qty[line.id], serials } })} /> : null}
				</td>
				<td style={{ ...ui.td, ...ui.right }}>{line.requested}</td>
				<td style={{ ...ui.td, ...ui.right }}>{action === "dispatch" ? line.originOnHand : line.sent}</td>
				{action === "dispatch" ? <td style={ui.td}><input type="number" min={0} max={line.originOnHand} className="form-control" style={ui.input} value={qty[line.id]?.a || ""} disabled={busy}
					onChange={(event) => setQty({ ...qty, [line.id]: { ...qty[line.id], a: event.target.value } })} /></td> : null}
				{action === "receive" ? <>
					<td style={ui.td}><input type="number" min={0} max={line.sent} className="form-control" style={ui.input} value={qty[line.id]?.a || ""} disabled={busy} onChange={(event) => setQty({ ...qty, [line.id]: { ...qty[line.id], a: event.target.value } })} /></td>
					<td style={ui.td}><input type="number" min={0} className="form-control" style={ui.input} value={qty[line.id]?.b || ""} disabled={busy} onChange={(event) => setQty({ ...qty, [line.id]: { ...qty[line.id], b: event.target.value } })} /></td>
				</> : null}
				{action === null && st >= S.RECEIVED ? <td style={{ ...ui.td, ...ui.right }}>{line.received}{line.damaged ? <div style={{ ...ui.tiny, color: "var(--wa-oro)" }}>{line.damaged} dañadas</div> : null}{line.missing ? <div style={{ ...ui.tiny, color: "var(--wa-oro)" }}>faltan {line.missing}</div> : null}</td> : null}
			</tr>)}</tbody>
		</table>

		{action === "dispatch" || action === "receive" ? <>
			<div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "10px" }}>
				<PhotoInput caption={action === "dispatch" ? "Fotos de lo que sale *" : "Fotos de lo que llegó *"} photos={photos} busy={busy} onChange={setPhotos} />
				{action === "dispatch" ? <div style={ui.field("150px")}><span style={ui.caption}>Llega el</span>
					<input type="date" className="form-control" style={ui.input} value={expectedAt} disabled={busy} onChange={(event) => setExpectedAt(event.target.value)} /></div> : null}
				<div style={{ flex: "1 1 240px" }}><span style={ui.caption}>Nota</span>
					<input type="text" className="form-control" style={ui.input} maxLength={1000} value={note} disabled={busy} onChange={(event) => setNote(event.target.value)} /></div>
			</div>
			{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
			<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "12px" }}>
				<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={() => setAction(null)}>Regresar</button>
				<button type="button" className="btn btn-primary rounded-pill" disabled={busy || photos.length < 1} onClick={submit}>{busy ? "Guardando…" : action === "dispatch" ? "Despachar" : "Registrar Recepción"}</button>
			</div>
		</> : null}

		{transfer.files.length > 0 ? <div style={{ marginTop: "12px" }}>
			<div style={{ fontWeight: 700, fontSize: "14px" }}>Fotos</div>
			{transfer.files.map((file, index) => <button key={file.id} type="button" className="btn btn-link btn-sm" style={{ padding: "0 8px 0 0" }}
				onClick={() => http.transferFile(campusId, transfer.id, file.id).then((response) => openBlob(response.data, "Foto del traslado")).catch(() => setError("No se pudo abrir la foto."))}>
				{file.stage === "dispatch" ? "Salida" : "Llegada"} {index + 1}</button>)}
		</div> : null}
		{transfer.note ? <div style={{ ...ui.small, marginTop: "8px" }}>Nota: {transfer.note}</div> : null}
		<div style={{ marginTop: "12px" }}>
			<div style={{ fontWeight: 700, fontSize: "14px" }}>Historial</div>
			{transfer.history.map((entry, index) => <div key={index} style={{ fontSize: "13px", padding: "3px 0" }}>
				<span style={ui.small}>{entry.createdAt}</span> · {entry.summary}{entry.note ? <div style={{ ...ui.small, paddingLeft: "10px" }}>{entry.note}</div> : null}
			</div>)}
		</div>
		{error && action === null ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
		{action === "cancel" ? <ReasonPrompt title={"Cancelar " + transfer.folio} label="Motivo" confirmLabel="Cancelar traslado" confirmClass="btn-primary" busy={busy} error={error}
			onCancel={() => setAction(null)} onConfirm={(reason) => run(() => http.cancelTransfer(campusId, transfer.id, reason))} /> : null}
	</Modal>;
};

export default Traslados;
